import type { QueryClient } from '@tanstack/react-query';
import type { PlApiClient, Status } from 'pl-api';

// Verification has no automatic writes to the canonical status query: callers
// must first check that the cached body has not changed while awaiting it.
const requests = new WeakMap<PlApiClient, Map<string, Promise<Status>>>();

const COOLDOWN = 60_000;
const WINDOW_BUDGET = 24;
const REFRESH_BUDGET = 12;
type Budget = { since: number; used: number; checked: Record<string, number> };
const budgetKey = (scope: string) => [scope, 'status-verification-budget'] as const;
const readBudget = (cache: QueryClient, scope: string): Budget => {
  const now = Date.now();
  const current = cache.getQueryData<Budget>(budgetKey(scope));
  return !current || now < current.since || now - current.since >= COOLDOWN
    ? { since: now, used: 0, checked: current?.checked ?? {} }
    : current;
};
const isDue = (checked?: number) =>
  checked === undefined || Date.now() < checked || Date.now() - checked >= COOLDOWN;
const requestKey = (scope: string, id: string) => JSON.stringify([scope, id]);

/** Unchecked and oldest-checked nodes go first, so large partial contexts progress. */
const selectStatusVerifications = (
  client: PlApiClient,
  cache: QueryClient,
  scope: string,
  ids: string[],
) => {
  const budget = readBudget(cache, scope);
  return ids
    .filter(
      (id) =>
        requests.get(client)?.has(requestKey(scope, id)) ||
        (budget.used < WINDOW_BUDGET && isDue(budget.checked[id])),
    )
    .toSorted(
      (a, b) =>
        (budget.checked[a] ?? -Infinity) - (budget.checked[b] ?? -Infinity) || a.localeCompare(b),
    )
    .slice(0, REFRESH_BUDGET);
};

const fetchStatusVerification = (
  client: PlApiClient,
  cache: QueryClient,
  scope: string,
  id: string,
) => {
  let pending = requests.get(client);
  if (!pending) requests.set(client, (pending = new Map()));
  const key = requestKey(scope, id);
  let request = pending.get(key);
  if (!request) {
    const budget = readBudget(cache, scope);
    if (budget.used >= WINDOW_BUDGET || !isDue(budget.checked[id])) return;
    // Charge only work actually started; cancelled queued nodes stay eligible.
    cache.setQueryData<Budget>(budgetKey(scope), {
      ...budget,
      used: budget.used + 1,
      checked: { ...budget.checked, [id]: Date.now() },
    });
    request = client.statuses.getStatus(id);
    pending.set(key, request);
    const release = () => pending.delete(key);
    void request.then(release, release);
  }
  return request;
};

export { fetchStatusVerification, selectStatusVerifications };
